import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Switch,
  Select,
  FormControl,
  Typography,
  IconButton,
  Grid,
  Box,
  TextField,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import DatePickerComponent from "../../../../../../../common/components/date-picker/date-picker-compoenent";
import {
  AlertComponent,
  TextInputComponent,
} from "../../../../../../../common/components";
import {
  TASK_DIALOG_INITIAL_VALUES,
  TASK_DIALOG_VALIDATION_SCHEMA,
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  getFullName,
} from "./common-dialog-helper";
import SearchableSelectOwner from "../../../../../../../common/components/select-owner/select-owner-compoent";
import { getCurrentUser } from "../../../../../../../common/routines/user-operations";
import SelectInputComponent from "../../../../../../../common/components/select-input-component/select-input-component";
import dayjs from "dayjs";
import "./common-dialog-task.scss";
import { getUserDetails } from "../../../../../../../api/lead";
import { taskCreate, editTask } from "../../../../../../../api/campaign";
import { useSetLoading } from "../../../../../../../common/hooks/appearance";

interface CommonTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: any) => void;
  title?: string;
  customerDetails?: any;
  initialTask?: any;
}
export interface OwnerOption {
  value: string;
  label: string;
  email: string;
  avatar?: string | null;
}

type ReminderTimeType = "hour" | "minute";
type ReminderTimeField = "reminderHour" | "reminderMinute";

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) =>
  (i + 1).toString().padStart(2, "0"),
);
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) =>
  i.toString().padStart(2, "0"),
);

const CommonTaskDialog: React.FC<CommonTaskDialogProps> = ({
  open,
  onClose,
  onSave,
  customerDetails,
  title = "Create Task",
  initialTask,
}) => {
  const [leadOptions, setLeadOptions] = useState<OwnerOption[]>([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState<boolean>(false);
  const [formInitialValues, setFormInitialValues] = useState(
    TASK_DIALOG_INITIAL_VALUES,
  );
  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const getDisplayTimeValue = (value: string) =>
    (value || "00").toString().padStart(2, "0");

  const formatTwoDigitTimeInput = (
    newInputValue: string,
    currentValue: string,
    isDeleteAction: boolean,
    fieldType: ReminderTimeType,
  ) => {
    const digitsOnly = (newInputValue || "").replace(/\D/g, "");
    const normalizedCurrentValue = getDisplayTimeValue(currentValue)
      .replace(/\D/g, "")
      .slice(-2);

    if (!digitsOnly) return "00";

    let normalizedValue = "";
    if (isDeleteAction) {
      normalizedValue = digitsOnly.padStart(2, "0").slice(-2);
    } else if (digitsOnly.length > 2) {
      normalizedValue = digitsOnly.startsWith("0")
        ? digitsOnly.slice(-2)
        : digitsOnly.slice(0, 2);
    } else if (digitsOnly.length === 2) {
      normalizedValue = digitsOnly;
    } else {
      normalizedValue = `${normalizedCurrentValue[1]}${digitsOnly}`;
    }

    const numericValue = parseInt(normalizedValue, 10);
    if (Number.isNaN(numericValue)) return "00";

    if (fieldType === "hour") {
      if (normalizedValue === "00") return "00";
      if (numericValue > 12) return "12";
      if (numericValue < 1) return "01";
      return numericValue.toString().padStart(2, "0");
    }

    if (numericValue > 59) return "59";
    if (numericValue < 0) return "00";
    return numericValue.toString().padStart(2, "0");
  };

  const getNextRollingTimeValue = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    currentValue: string,
    fieldType: ReminderTimeType,
  ) => {
    const nativeInputType = (event.nativeEvent as InputEvent)?.inputType || "";
    return formatTwoDigitTimeInput(
      event.target.value || "",
      getDisplayTimeValue(currentValue),
      nativeInputType.includes("delete"),
      fieldType,
    );
  };

  const getReminderTimeFieldValue = (
    values: any,
    field: ReminderTimeField,
  ) => getDisplayTimeValue(values?.[field]);

  const handleReminderTimeOptionChange = (
    setFieldValue: any,
    values: any,
    field: ReminderTimeField,
    fieldType: ReminderTimeType,
    newValue: string | null,
  ) => {
    setFieldValue(
      field,
      formatTwoDigitTimeInput(
        String(newValue || ""),
        getReminderTimeFieldValue(values, field),
        false,
        fieldType,
      ),
    );
  };

  const handleReminderTimeInputChange = (
    setFieldValue: any,
    values: any,
    field: ReminderTimeField,
    fieldType: ReminderTimeType,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFieldValue(
      field,
      getNextRollingTimeValue(
        event,
        getReminderTimeFieldValue(values, field),
        fieldType,
      ),
    );
  };

  const computedInitialValues = useMemo(() => {
    if (initialTask) {
      return {
        subject: initialTask.subject || "",
        leadOwner: initialTask.owner?.id ? String(initialTask.owner.id) : "",
        dueDate: initialTask.due_date ? dayjs(initialTask.due_date) : null,
        priority: initialTask.priority || "High",
        relatedTo:
          getFullName(
            customerDetails?.first_name,
            customerDetails?.middle_name,
            customerDetails?.last_name,
          ) || "",
        status: initialTask.status || "Not Started",
        description: initialTask.description || "",
        reminder: !!initialTask.reminder,
        reminderDate: initialTask.reminder ? dayjs(initialTask.reminder) : null,
        reminderHour: initialTask.reminder
          ? dayjs(initialTask.reminder).format("hh")
          : "",
        reminderMinute: initialTask.reminder
          ? dayjs(initialTask.reminder).format("mm")
          : "",
        reminderAmPm:
          initialTask.reminder &&
          dayjs(initialTask.reminder).format("A") === "PM"
            ? "PM"
            : "AM",

        repeat: false,
        repeatType: "Daily",
        ends: "never",
        endsAfter: 1,
      };
    } else {
      const currentUserId = getCurrentUser();
      const matchedUser = leadOptions.find(
        (opt) => opt.value === String(currentUserId?.id),
      );

      return {
        ...TASK_DIALOG_INITIAL_VALUES,
        leadOwner: matchedUser?.value || "",
        relatedTo:
          getFullName(
            customerDetails?.first_name,
            customerDetails?.middle_name,
            customerDetails?.last_name,
          ) || "",
      };
    }
  }, [initialTask, customerDetails, leadOptions]);

  const handleSave = async (values: any) => {
    console.log("values", values);
    try {
      setLoading(true);

      // Format reminder time if enabled
      let reminderDateTime = null;
      if (values.reminder && values.reminderDate) {
        const hour = values.reminderHour || "00";
        const minute = values.reminderMinute || "00";
        const amPm = values.reminderAmPm || "AM";
        let hour24 = parseInt(hour, 10);

        if (amPm === "PM" && hour24 < 12) hour24 += 12;
        if (amPm === "AM" && hour24 === 12) hour24 = 0;

        reminderDateTime = dayjs(values.reminderDate)
          .hour(hour24)
          .minute(parseInt(minute))
          .second(0)
          .millisecond(0)
          .toISOString();
      }

      const payload = {
        subject: values.subject,
        ownerId: parseInt(values.leadOwner, 10),
        dueDate: values.dueDate ? dayjs(values.dueDate).toISOString() : null,
        priority: values.priority,
        status: values.status.replace(/\s+/g, ""),
        description: values.description,
        reminder: reminderDateTime,
        reminderDescription: "",
        ...(initialTask?.related_to_lead_id
          ? { relatedToLeadId: initialTask.related_to_lead_id }
          : { relatedToCustomerId: customerDetails?.id }),
      };

      let response;
      if (initialTask?.id) {
        response = await editTask(initialTask.id, payload);
        setAlertMessage("Task updated successfully.");
      } else {
        response = await taskCreate(payload, "Lead Details");
        setAlertMessage("Task created successfully.");
      }

      setAlertType("success");
      setAlertOpen(true);
      onSave(response?.data || values);
      onClose();
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "Failed to save task. Please try again.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCreatedByOptions = async () => {
      try {
        const response = await getUserDetails({}, "Customer Profile");
        const data = response.data.data.data.data;
        const formattedOptions: OwnerOption[] = data.map((user: any) => ({
          value: String(user.id),
          label: `${user.first_name} ${user.last_name}`,
          email: user.email,
          avatar: user.profile_image_url || null,
        }));

        setLeadOptions(formattedOptions);

        // Set default lead owner
        const currentUserId = getCurrentUser();
        const matchedUser = formattedOptions.find(
          (opt) => opt.value === String(currentUserId?.id),
        );
        if (matchedUser) {
          setFormInitialValues((prev: any) => ({
            ...prev,
            leadOwner: matchedUser.value,
            relatedTo: customerDetails?.name || "",
          }));
        }
      } catch (error: any) {
        setAlertMessage(
          error?.response?.data?.message ||
            "Something went wrong please try again.",
        );
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    };

    fetchCreatedByOptions();
  }, [customerDetails]);
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              minWidth: "640px",
              // minHeight: "108px",
              position: "absolute",
              top: 0,
              margin: 0,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 1,
            paddingLeft: 3,
            borderBottom: "1px solid #E1E1E1",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold !important" }} // You can also use 700 or 'bold'
          >
            {title}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          enableReinitialize
          initialValues={computedInitialValues}
          validationSchema={TASK_DIALOG_VALIDATION_SCHEMA}
          onSubmit={(values) => handleSave(values)}
        >
          {({ values, handleChange, setFieldValue, setFieldTouched, touched, errors }) => (
            <Form>
              <div className="task-dialog-container">
                <DialogContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={6}>
                        <Typography width={100}>Subject</Typography>
                        <Box flex={1}>
                          <TextInputComponent
                            type="text"
                            fieldName="subject"
                            placeholder="Enter Subject"
                            optional={false}
                            disabled={false}
                            value={values.subject}
                            onChange={handleChange}
                            maxlength="100"
                            isRequired={true}
                          />
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={6}>
                        <Typography width={100}>Owner</Typography>
                        <Box flex={1}>
                          <SearchableSelectOwner
                            fieldName="leadOwner"
                            options={leadOptions}
                            isShowError={!values?.leadOwner}
                            placeholder="Select Owner"
                            optional={true}
                            value={
                              leadOptions.find(
                                (opt) => opt.value === values?.leadOwner,
                              ) || null
                            }
                            onChange={(selectedOption: OwnerOption | null) => {
                              if (selectedOption) {
                                setFieldValue(
                                  "leadOwner",
                                  selectedOption.value,
                                ); // saving only the ID
                              } else {
                                setFieldValue("leadOwner", null); // handle null case
                              }
                            }}
                            handleBlur={() => {
                              setFieldTouched("leadOwner", true);
                            }}
                            isRequired={true}
                          />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={6}>
                        <Typography width={100}>Due Date</Typography>
                        <Box flex={1}>
                          <DatePickerComponent
                            label="Due Date"
                            fieldName="dueDate"
                            value={values?.dueDate || null}
                            onChange={(date) => setFieldValue("dueDate", date)}
                            minDate={dayjs("1900-01-01")}
                            // maxDate={values?.dueDate || undefined}
                            optional={true}
                            isLabel={false}
                          />
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={6}>
                        <Typography width={100}>Priority</Typography>
                        <Box flex={1}>
                          <SelectInputComponent
                            fieldName="priority"
                            value={values?.priority}
                            onChange={(e: any) => {
                              setFieldValue("priority", e);
                            }}
                            options={PRIORITY_OPTIONS}
                            optional={true}
                            isShowError={!values.priority}
                            placeholder="Select Priority"
                          />
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={6}
                        className="campaign-input-component"
                      >
                        <Typography width={100}>Related to</Typography>
                        <Box flex={1}>
                          <TextField
                            name={"relatedTo"}
                            placeholder="Select Leads"
                            id="relatedTo"
                            value={values.relatedTo}
                            onChange={handleChange}
                            type="text"
                            disabled
                          />
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={6}>
                        <Typography width={100}>Status</Typography>
                        <Box flex={1}>
                          <SelectInputComponent
                            fieldName="status"
                            value={values?.status}
                            onChange={(e: any) => {
                              setFieldValue("status", e);
                            }}
                            options={STATUS_OPTIONS}
                            optional={true}
                            isShowError={!values.status}
                            placeholder="Select status"
                          />
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={6}>
                        <Typography width={100}>Description</Typography>
                        <Box flex={1}>
                          <TextInputComponent
                            type="text"
                            fieldName="description"
                            placeholder="Add Description"
                            optional={false}
                            disabled={false}
                            value={values.description}
                            onChange={handleChange}
                            maxlength="100"
                          />
                        </Box>
                      </Box>
                    </Grid>

                    {/* Reminder */}
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={5}>
                        {/* Label */}
                        <Typography width={100}>Reminder</Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          {/* Toggle */}
                          <Switch
                            checked={values.reminder}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFieldValue("reminder", checked);

                              if (!checked) {
                                // Clear all reminder-related fields
                                setFieldValue("reminderDate", null);
                                setFieldValue("reminderHour", "");
                                setFieldValue("reminderMinute", "");
                                setFieldValue("reminderAmPm", "AM");
                              }
                            }}
                          />

                          {/* Only show date & time inputs when reminder is ON */}
                          {values.reminder && (
                            <>
                              {/* Date */}
                              <Box
                                sx={{ width: 150, marginRight: "5px" }}
                                className="date-remainder"
                              >
                                <DatePickerComponent
                                  label="Date"
                                  fieldName="reminderDate"
                                  value={values.reminderDate}
                                  onChange={(date) => {
                                    setFieldValue("reminderDate", date);
                                    if (
                                      date &&
                                      dayjs(date).isSame(dayjs(), "day")
                                    ) {
                                      // Set defaults to next 30 minutes if selected date is today
                                      const now = dayjs();
                                      const next30 = now.add(30, "minute");
                                      const hour = next30.hour();
                                      const ampm = hour >= 12 ? "PM" : "AM";
                                      let hour12 = hour % 12;
                                      if (hour12 === 0) hour12 = 12;
                                      const minute = next30.minute();
                                      setFieldValue(
                                        "reminderHour",
                                        hour12.toString().padStart(2, "0"),
                                      );
                                      setFieldValue(
                                        "reminderMinute",
                                        minute.toString().padStart(2, "0"),
                                      );
                                      setFieldValue("reminderAmPm", ampm);
                                    } else {
                                      // Clear time fields for future dates
                                      setFieldValue("reminderHour", "");
                                      setFieldValue("reminderMinute", "");
                                      setFieldValue("reminderAmPm", "AM");
                                    }
                                  }}
                                  minDate={dayjs()}
                                  optional={true}
                                  isLabel={false}
                                  error={
                                    !!(
                                      touched.reminderDate &&
                                      errors.reminderDate
                                    )
                                  }
                                  useFormikError={false}
                                />
                              </Box>

                              {/* Hour */}
                              <Autocomplete
                                freeSolo
                                disableClearable
                                className="reminder-minute-autocomplete"
                                options={HOUR_OPTIONS}
                                value={getReminderTimeFieldValue(values, "reminderHour")}
                                inputValue={getReminderTimeFieldValue(values, "reminderHour")}
                                onChange={(event, newValue) => {
                                  handleReminderTimeOptionChange(
                                    setFieldValue,
                                    values,
                                    "reminderHour",
                                    "hour",
                                    newValue,
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    name="reminderHour"
                                    type="text"
                                    value={getReminderTimeFieldValue(values, "reminderHour")}
                                    onChange={(e) => {
                                      handleReminderTimeInputChange(
                                        setFieldValue,
                                        values,
                                        "reminderHour",
                                        "hour",
                                        e,
                                      );
                                    }}
                                    inputProps={{
                                      ...params.inputProps,
                                      style: { width: 50, textAlign: "center" },
                                      maxLength: 3,
                                      inputMode: "numeric",
                                      pattern: "[0-9]*",
                                      onWheel: (e) => e.currentTarget.blur(),
                                    }}
                                  />
                                )}
                              />

                              <Typography sx={{ marginRight: "5px" }}>
                                :
                              </Typography>

                              <Autocomplete
                                freeSolo
                                disableClearable
                                className="reminder-minute-autocomplete"
                                options={MINUTE_OPTIONS}
                                value={getReminderTimeFieldValue(values, "reminderMinute")}
                                inputValue={getReminderTimeFieldValue(values, "reminderMinute")}
                                onChange={(event, newValue) => {
                                  handleReminderTimeOptionChange(
                                    setFieldValue,
                                    values,
                                    "reminderMinute",
                                    "minute",
                                    newValue,
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    name="reminderMinute"
                                    type="text"
                                    value={getReminderTimeFieldValue(values, "reminderMinute")}
                                    onChange={(e) => {
                                      handleReminderTimeInputChange(
                                        setFieldValue,
                                        values,
                                        "reminderMinute",
                                        "minute",
                                        e,
                                      );
                                    }}
                                    inputProps={{
                                      ...params.inputProps,
                                      style: { width: 50, textAlign: "center" },
                                      maxLength: 3,
                                      inputMode: "numeric",
                                      pattern: "[0-9]*",
                                      onWheel: (e) => e.currentTarget.blur(),
                                    }}
                                  />
                                )}
                              />

                              {/* AM/PM */}
                              <FormControl size="small">
                                <Select
                                  name="reminderAmPm"
                                  value={values.reminderAmPm}
                                  onChange={handleChange}
                                  sx={{
                                    width: 70,
                                    "& .MuiSelect-select": {
                                      paddingRight: "0 !important", // remove padding right forcibly
                                    },
                                  }}
                                >
                                  <MenuItem value="AM">AM</MenuItem>
                                  <MenuItem value="PM">PM</MenuItem>
                                </Select>
                              </FormControl>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </DialogContent>

                <DialogActions sx={{ margin: "12px" }}>
                  <button onClick={onClose} className="cancel-button">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="cancel-button next-button-wrapper"
                  >
                    Save
                  </button>
                </DialogActions>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={handleAlertClose}
      />
    </>
  );
};

export default CommonTaskDialog;
